import React, { useState } from 'react';
import { CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import { IconDownload, IconEye } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { knowledgeBaseApi } from '@shared/services/apiService/knowledgeBaseApi';
import { getKnowledgeBaseErrorMessageKey } from '@features/Admin/KnowledgeBase/model/error-mapper';

import { useStyles } from '../KnowledgeBaseTab.styles';

interface KnowledgeBaseOriginalActionsProps {
  fileId: string;
  fileName: string;
  hasOriginal: boolean;
  canManageFiles: boolean;
  showDownload?: boolean;
}

const KnowledgeBaseOriginalActions: React.FC<KnowledgeBaseOriginalActionsProps> = ({
  fileId,
  fileName,
  hasOriginal,
  canManageFiles,
  showDownload = true,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [getOriginal, { isFetching }] = knowledgeBaseApi.useLazyFileOriginalControllerGetQuery();
  const [activeAction, setActiveAction] = useState<'view' | 'download' | null>(null);

  if (!canManageFiles || !hasOriginal) return null;

  const handleView = async () => {
    if (isFetching) return;

    const previewWindow = window.open('', '_blank');
    if (!previewWindow) {
      toast.error(t('campaignPage.knowledgeBase.errors.original.generic'));
      return;
    }

    previewWindow.opener = null;
    setActiveAction('view');
    try {
      const { url } = await getOriginal({ fileId }).unwrap();
      previewWindow.location.replace(url);
    } catch (error) {
      previewWindow.close();
      toast.error(t(getKnowledgeBaseErrorMessageKey(error, 'original')));
    } finally {
      setActiveAction(null);
    }
  };

  const handleDownload = async () => {
    if (isFetching) return;

    setActiveAction('download');
    try {
      const { url } = await getOriginal({ fileId }).unwrap();
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Original PDF download failed: ${response.status}`);

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      toast.error(t(getKnowledgeBaseErrorMessageKey(error, 'original')));
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <Stack direction="row" spacing={0.25}>
      <Tooltip title={t('campaignPage.knowledgeBase.actions.viewOriginal')}>
        <span>
          <IconButton
            size="small"
            aria-label={t('campaignPage.knowledgeBase.actions.viewOriginal')}
            onClick={() => void handleView()}
            disabled={isFetching}
            className={classes.actionButton}>
            <IconEye size={18} stroke={1.5} />
          </IconButton>
        </span>
      </Tooltip>
      {showDownload && (
        <Tooltip title={t('campaignPage.knowledgeBase.actions.downloadOriginal')}>
          <span>
            <IconButton
              size="small"
              aria-label={t('campaignPage.knowledgeBase.actions.downloadOriginal')}
              onClick={() => void handleDownload()}
              disabled={isFetching}
              className={classes.actionButton}>
              {activeAction === 'download' ? (
                <CircularProgress
                  size={16}
                  color="inherit"
                  aria-label={t('campaignPage.knowledgeBase.actions.downloadingOriginal')}
                />
              ) : (
                <IconDownload size={18} stroke={1.5} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};

export default KnowledgeBaseOriginalActions;
